// Apple Reminders as device-local task lists (plan option A; spec §7.5;
// contract §4). Native app only.
//
// Reminders never touch the server: they are not in AppContext's persisted
// `todos`, never in pendingTodoMutations, never in a todos/profile payload.
// AppContext shows them as `source: 'apple'` lists and routes their edits
// here, straight to EventKit. iCloud syncs them between the user's devices.
//
// PRIVACY: imports the EventKit wrapper, so it must never import a network
// client (privacyInvariants rule 4).

import {
  completeReminder,
  deleteReminder,
  fetchReminders,
  getEventKitStatus,
  isEventKitDenied,
  listReminderLists,
  upsertReminder,
  type AppleReminder,
  type AppleReminderList,
  type ReminderPriority,
} from '../services/appleEventKit';
import {
  appleInUse,
  getAppleSettings,
  loadAppleSettings,
  subscribeAppleSettings,
  updateAppleSettings,
} from '../features/Calendar/appleSettings';
import { isNativeApp, onNativeEvent } from '../utils/nativeApp';
import { log } from '../utils/logger';
import type { Todo, TodoList, TodoPriority } from '../types';

/** Rhemn ids for Apple rows: never digit-only, never a server id. */
export const APPLE_ID_PREFIX = 'apple:';
export const isAppleId = (id: string | undefined | null): boolean => !!id && id.startsWith(APPLE_ID_PREFIX);
const rawId = (id: string): string => id.slice(APPLE_ID_PREFIX.length);

export interface AppleRemindersSnapshot {
  lists: AppleReminderList[];
  reminders: AppleReminder[];
  /** Reminders access was turned off in Settings while connected (§7.5). */
  accessOff: boolean;
}

const EMPTY: AppleRemindersSnapshot = { lists: [], reminders: [], accessOff: false };

let snapshot: AppleRemindersSnapshot = EMPTY;
const listeners = new Set<() => void>();
let generation = 0;
let tempSeq = 0;

const emit = (next: AppleRemindersSnapshot): void => {
  snapshot = next;
  listeners.forEach((l) => l());
};

export const subscribeAppleReminders = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export const getAppleRemindersSnapshot = (): AppleRemindersSnapshot => snapshot;

/** The chosen lists, or the default Reminders list when nothing was chosen (§5.3). */
export const resolveReminderListIds = (all: readonly AppleReminderList[]): string[] => {
  const chosen = getAppleSettings().reminderListIds;
  if (chosen) return chosen.filter((id) => all.some((l) => l.id === id));
  return all.filter((l) => l.isDefault).map((l) => l.id);
};

/** Re-read the chosen lists from EventKit. Silent on failure. */
export const reloadAppleReminders = async (): Promise<void> => {
  const myGen = ++generation;
  if (!isNativeApp()) return;
  await loadAppleSettings();
  if (!appleInUse()) {
    if (myGen === generation && snapshot !== EMPTY) emit(EMPTY);
    return;
  }
  try {
    const status = await getEventKitStatus('reminders');
    if (status !== 'fullAccess') {
      if (myGen === generation) emit({ ...EMPTY, accessOff: status === 'denied' || status === 'restricted' });
      return;
    }
    const all = await listReminderLists();
    const ids = resolveReminderListIds(all);
    const reminders = await fetchReminders(ids, true);
    if (myGen !== generation) return;
    emit({ lists: ids.map((id) => all.find((l) => l.id === id)).filter((l): l is AppleReminderList => !!l), reminders, accessOff: false });
  } catch (err) {
    log('[appleReminders] reload failed:', err as Error);
    if (myGen === generation && isEventKitDenied(err)) emit({ ...EMPTY, accessOff: true });
  }
};

const replace = (id: string, next: AppleReminder | null): void => {
  emit({
    ...snapshot,
    reminders: next
      ? snapshot.reminders.map((r) => (r.id === id ? next : r))
      : snapshot.reminders.filter((r) => r.id !== id),
  });
};

/** A failed write: EventKit is the truth, so re-read it. */
const recover = (err: unknown): void => {
  log('[appleReminders] write failed; re-reading EventKit:', err as Error);
  void reloadAppleReminders();
};

// ── CRUD (called by AppContext for `apple:` ids) ─────────────────────────────

export const addAppleReminder = (listId: string, title: string, priority: TodoPriority): void => {
  const text = title.trim();
  if (!text || !isAppleId(listId)) return;
  const temp: AppleReminder = {
    id: `pending-${Date.now()}-${tempSeq++}`,
    listId: rawId(listId),
    title: text,
    completed: false,
    completedAt: null,
    dueDate: null,
    priority,
    updatedAt: new Date().toISOString(),
  };
  emit({ ...snapshot, reminders: [...snapshot.reminders, temp] });
  upsertReminder({ listId: temp.listId, title: text, priority })
    .then((saved) => replace(temp.id, saved))
    .catch(recover);
};

export const setAppleReminderCompleted = (todoId: string, completed: boolean): void => {
  const id = rawId(todoId);
  const current = snapshot.reminders.find((r) => r.id === id);
  if (!current) return;
  replace(id, { ...current, completed, completedAt: completed ? new Date().toISOString() : null });
  completeReminder(id, completed).then((saved) => replace(id, saved)).catch(recover);
};

export const updateAppleReminder = (todoId: string, title: string, priority?: ReminderPriority): void => {
  const id = rawId(todoId);
  const current = snapshot.reminders.find((r) => r.id === id);
  if (!current || !title.trim()) return;
  const next = { ...current, title: title.trim(), ...(priority ? { priority } : {}) };
  replace(id, next);
  upsertReminder({ id, listId: current.listId, title: next.title, priority: next.priority })
    .then((saved) => replace(id, saved))
    .catch(recover);
};

export const deleteAppleReminder = (todoId: string): void => {
  const id = rawId(todoId);
  if (!snapshot.reminders.some((r) => r.id === id)) return;
  replace(id, null);
  deleteReminder(id).catch(recover);
};

/** "Remove from Rhemn": stop showing the list here. Nothing changes in Reminders. */
export const hideAppleReminderList = (listId: string): void => {
  const id = rawId(listId);
  const all = snapshot.lists.map((l) => l.id);
  updateAppleSettings({ reminderListIds: (getAppleSettings().reminderListIds ?? all).filter((x) => x !== id) });
};

// ── Mapping into the Tasks model ─────────────────────────────────────────────

export const appleTodoLists = (s: AppleRemindersSnapshot): TodoList[] =>
  s.lists.map((l) => ({
    id: `${APPLE_ID_PREFIX}${l.id}`,
    userId: '',
    name: l.name,
    createdAt: '',
    updatedAt: '',
    source: 'apple',
    provider: 'apple',
  }));

export const appleTodos = (s: AppleRemindersSnapshot): Todo[] =>
  s.reminders.map((r) => ({
    id: `${APPLE_ID_PREFIX}${r.id}`,
    listId: `${APPLE_ID_PREFIX}${r.listId}`,
    text: r.title,
    completed: r.completed,
    priority: r.priority,
    source: 'apple',
    dueDate: r.dueDate,
  }));

// ── Lifecycle ────────────────────────────────────────────────────────────────

let installed = false;

/** Once, native only: reload on EventKit changes, app focus and settings changes. */
export const installAppleReminders = (): void => {
  if (installed || !isNativeApp()) return;
  installed = true;
  let last = '';
  subscribeAppleSettings(() => {
    const s = getAppleSettings();
    const next = `${appleInUse()}|${(s.reminderListIds ?? ['*']).join(',')}`;
    if (next === last) return;
    last = next;
    void reloadAppleReminders();
  });
  onNativeEvent('eventkit.changed', () => { void reloadAppleReminders(); });
  onNativeEvent('app.becameActive', () => { void reloadAppleReminders(); });
};

/** Sign-out / account switch: drop everything held in memory. */
export const resetAppleReminders = (): void => {
  generation += 1;
  emit(EMPTY);
};
